import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@ui/table.tsx';
import { Card, CardContent, CardHeader } from '@r/components/ui/card.tsx';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@ui/tooltip.tsx';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@ui/dialog.tsx';
import { Button } from '@ui/button.tsx';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import pointsTranslation from '@r/translations/points-translation.ts';
import { cn } from '@r/utils/utils.ts';
import type {
  OrderOrderReadModel,
  OrderProductOrderReadModel
} from '@r/api/generated/client.schemas.ts';
import shopDefaultBanner from '@r/assets/images/fiddy/default-shop-banner.png';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { InfoIcon } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { FormattedNumber } from '@r/components/formatted-number.tsx';
import { FormattedHtml } from '@r/components/formatted-html.tsx';

type OrderProductsTableCardProps = {
  order: OrderOrderReadModel;
  admin: boolean;
  onProductRowClick?: (productId: number) => void;
};

export function OrderProductsTableCard({
  order,
  admin,
  onProductRowClick
}: OrderProductsTableCardProps) {
  const { t } = useTranslation();
  const [selectedItem, setSelectedItem] = useState<OrderProductOrderReadModel | null>(null);

  const handleCopyActivationCode = (code: string) => {
    navigator.clipboard
      .writeText(code)
      .then(() => toast.success(t('common.clipboard.success')))
      .catch(() => toast.error(t('common.clipboard.error')));
  };
  // Le prix unitaire monetaire n'a de sens que pour les produits a prix dynamique
  // (cheques cadeaux). On masque la colonne entiere si aucun item n'est concerne.
  const showAmountUnitPrice = order.orderProducts.some((item) => item.product.dynamicPrice);

  const statusStyles = {
    'order.status.pending': 'bg-warning/20 text-warning border-warning/30',
    'order.status.confirmed': 'bg-info/20 text-info border-info/30',
    'order.status.completed': 'bg-success/20 text-success border-success/30',
    'order.status.canceled': 'bg-muted text-muted-foreground border-muted-foreground/10',
    'order.status.failed': 'bg-destructive/20 text-destructive border-destructive/30'
  } as const;

  const OrderStatus = () => (
    <div
      className={cn(
        'inline-flex gap-2 justify-center items-center w-fit',
        'px-3.5 py-1 text-sm font-medium whitespace-nowrap rounded-full',
        statusStyles[order.status]
      )}
    >
      <span className="w-1.5 h-1.5 rounded-full bg-current shrink-0" />
      {t(order.status)}
    </div>
  );

  const OrderInfosLine = (label: string, value: ReactNode) => (
    <div className="flex flex-col col-span-12 md:col-span-6 lg:col-span-12 xl:col-span-6 h-fit">
      <span className="mr-3 text-sm font-medium uppercase text-muted-foreground">{t(label)}</span>
      <span className="font-medium">{value}</span>
    </div>
  );

  return (
    <Card className="overflow-hidden w-full">
      <CardHeader className="px-0 pt-0">
        <Card className="grid overflow-hidden grid-cols-12 rounded-none border-0 border-b h-fit">
          <div className="overflow-hidden relative col-span-12 aspect-square sm:aspect-auto sm:col-span-5 lg:col-span-6 xl:col-span-3">
            <div className="absolute inset-0 bg-gradient-to-t sm:bg-gradient-to-l from-muted-foreground/10 to-transparent to-50% z-10" />

            <img
              className="object-cover object-center absolute inset-0 w-full h-full scale-75 sm:scale-100"
              src={order.shops[0]?.logo?.pathUri || shopDefaultBanner}
              alt="shop logo"
              onError={(e) => {
                e.currentTarget.src = shopDefaultBanner;
                e.currentTarget.onerror = null;
              }}
            />
          </div>

          <div className="grid grid-cols-12 col-span-12 gap-5 p-6 sm:col-span-7 lg:col-span-6 xl:col-span-9 h-fit">
            <div className="flex flex-col col-span-12 break-all md:col-span-6 lg:col-span-12 xl:col-span-6 h-fit">
              <span className="text-sm font-medium uppercase text-muted-foreground">
                {t('order.reference.short')}
              </span>

              {order.reference}
            </div>

            {OrderInfosLine('order.supplier.label', order.shops[0]?.displayName)}

            {OrderInfosLine(
              'order.date.created',
              format(new Date(order.createdAt), "d MMMM yyyy 'à' HH'h'mm", {
                locale: fr
              })
            )}

            {OrderInfosLine('order.items.count', order.productsCount)}

            {OrderInfosLine(
              'label.order.point.amount',
              pointsTranslation(t, Math.abs(order.point?.amount ?? 0), true)
            )}

            <div className="flex flex-col col-span-12 md:col-span-6 lg:col-span-12 xl:col-span-6 h-fit">
              <span className="text-sm font-medium uppercase text-muted-foreground">
                {t('label.order.status')}
              </span>

              <OrderStatus />
            </div>

            {order.trackingLink && order.trackingLink.filter(Boolean).length > 0 && (
              <div className="flex flex-col col-span-12 gap-1 md:col-span-6 lg:col-span-12 xl:col-span-6 h-fit">
                <span className="text-sm font-medium uppercase text-muted-foreground">
                  {t('label.order.tracking.link')}
                </span>
                <ul className="space-y-1">
                  {order.trackingLink.filter(Boolean).map((link) => (
                    <li key={link} className="flex items-center gap-1.5 text-sm">
                      <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                      <a
                        href={link as string}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-primary hover:underline"
                      >
                        {(() => {
                          try {
                            return new URL(link as string).hostname
                              .replace(/^www\./, '')
                              .replace(/\.[^.]+$/, '');
                          } catch {
                            return link;
                          }
                        })()}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </Card>
      </CardHeader>

      <CardContent className="p-6 pt-0">
        <div className="overflow-hidden border-0">
          <Table className="w-full">
            <TableHeader className="bg-transparent">
              <TableRow>
                <TableHead>{t('admin.order.detail.table.header.quantity')}</TableHead>
                <TableHead>{t('order.item')}</TableHead>
                <TableHead>{t('admin.order.detail.table.header.activation.code')}</TableHead>
                {showAmountUnitPrice && (
                  <TableHead className="text-right">{t('global.monetary.value')}</TableHead>
                )}
                <TableHead className="text-right">{t('global.unit.price')}</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody className="bg-transparent">
              {order.orderProducts.map((item, i) => {
                const hasActivationCode = !!item.orderProductCode?.code?.trim();
                const activationCode = hasActivationCode ? item.orderProductCode?.code : '-';

                const handleRowClick = admin
                  ? onProductRowClick
                    ? () => onProductRowClick(item.product.id)
                    : undefined
                  : hasActivationCode
                    ? () => setSelectedItem(item)
                    : undefined;

                return (
                  <TableRow
                    key={`${i}${item.product.id}`}
                    className={cn(
                      handleRowClick
                        ? 'transition-colors cursor-pointer hover:bg-accent/40'
                        : undefined
                    )}
                    onClick={handleRowClick}
                  >
                    <TableCell>{item.quantity}</TableCell>
                    <TableCell>
                      <p className="font-medium">{item.product.name}</p>
                      <FormattedHtml
                        text={item.product.designationCom}
                        className="text-sm text-muted-foreground line-clamp-2"
                      />
                    </TableCell>
                    <TableCell>
                      <span className="inline-flex gap-1.5 items-center">
                        {activationCode}
                        {hasActivationCode && (
                          <TooltipProvider delayDuration={0}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <InfoIcon
                                  className="text-muted-foreground size-4 shrink-0"
                                  aria-hidden
                                />
                              </TooltipTrigger>
                              <TooltipContent>
                                {t('admin.order.detail.table.activation.code.tooltip')}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </span>
                    </TableCell>
                    {showAmountUnitPrice && (
                      <TableCell className="text-right">
                        {item.product.dynamicPrice && (
                          <FormattedNumber value={item.amountUnitPrice} />
                        )}
                      </TableCell>
                    )}
                    <TableCell className="text-right">
                      {pointsTranslation(t, item.pointUnitPrice, true)}
                    </TableCell>
                  </TableRow>
                );
              })}
              <TableRow>
                <TableCell colSpan={showAmountUnitPrice ? 4 : 3} />
                <TableCell className="py-4 text-right">
                  <span className="flex gap-4 justify-end items-baseline">
                    <span className="text-sm text-muted-foreground">
                      {t('global.total.amount')}
                    </span>
                    <span className="text-lg font-bold">
                      {pointsTranslation(t, Math.abs(order.point?.amount ?? 0), true)}
                    </span>
                  </span>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      </CardContent>

      <Dialog open={!!selectedItem} onOpenChange={(open) => !open && setSelectedItem(null)}>
        <DialogContent>
          {selectedItem &&
            (() => {
              const publicMetadatas = selectedItem.product.publicMetadatas ?? [];
              const howtoText = publicMetadatas.find(
                (metadata) => metadata.metaKey === 'codeDetail'
              )?.metaValue;
              const howtoUrl = publicMetadatas.find(
                (metadata) => metadata.metaKey === 'howtoUrl'
              )?.metaValue;
              const code = selectedItem.orderProductCode?.code;
              const expiredAt = selectedItem.orderProductCode?.expiredAt;

              return (
                <>
                  <DialogHeader>
                    <DialogTitle>{t('order.reward.code.modal.title')}</DialogTitle>
                  </DialogHeader>

                  <div className="space-y-4 text-center">
                    <div>
                      <p className="text-lg font-semibold">{selectedItem.product.name}</p>
                      <FormattedHtml
                        text={selectedItem.product.designationCom}
                        className="text-sm text-muted-foreground"
                      />
                    </div>

                    <div className="rounded-2xl bg-muted/40 p-4 space-y-2">
                      <p className="text-xs font-medium uppercase text-muted-foreground">
                        {t('admin.order.reward.detail.activation.code')}
                      </p>
                      <div className="flex justify-between items-center px-4 py-2.5 bg-background rounded-xl border">
                        <span className="font-medium">{code}</span>
                        <Button
                          variant="raw"
                          type="button"
                          className="text-sm font-medium text-primary"
                          onClick={() => code && handleCopyActivationCode(code)}
                        >
                          {t('order.reward.code.modal.copy')}
                        </Button>
                      </div>
                      {expiredAt && (
                        <p className="text-xs text-muted-foreground">
                          {t('admin.order.reward.detail.activation.code.expires.at', {
                            date: new Date(expiredAt).toLocaleDateString()
                          })}
                        </p>
                      )}
                    </div>

                    {howtoText && (
                      <div className="space-y-1 text-left">
                        <p className="font-semibold">{t('order.reward.code.modal.howto.title')}</p>
                        <FormattedHtml
                          text={howtoText}
                          className="whitespace-pre-line text-muted-foreground"
                        />
                      </div>
                    )}
                  </div>

                  {howtoUrl && (
                    <a href={howtoUrl} target="_blank" rel="noopener noreferrer">
                      <Button rounded="full" className="w-full">
                        {t('order.reward.code.modal.cta')}
                      </Button>
                    </a>
                  )}
                </>
              );
            })()}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
